import React, { useState, useEffect } from 'react';
import { Target, Pencil, Check, X, ListChecks, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CommanServices } from '@/services/commanServices';
import { toast } from 'sonner';

/**
 * SimulationObjective
 * - Admin can set a Title + detailed Description + optional Success Criteria for the drill.
 * - Invited users / non-admins see it read-only.
 */
export const SimulationObjective = ({ incident, isAdmin, onRefresh }) => {
  const canEdit = !!isAdmin && !!incident?.is_exercise;
  const obj = incident?.simulation_objective || {};

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [form, setForm] = useState({ title: '', description: '', success_criteria: '' });

  useEffect(() => {
    setForm({
      title: obj.title || '',
      description: obj.description || '',
      success_criteria: obj.success_criteria || '',
    });
  }, [incident?.id, obj.title, obj.description, obj.success_criteria]);

  if (!incident?.is_exercise) return null;

  const hasContent = (obj.title || obj.description || obj.success_criteria);

  const handleSave = async () => {
    if (!form.title.trim() && !form.description.trim()) {
      toast.error('Add at least an objective title or description');
      return;
    }
    setSaving(true);
    try {
      const res = await CommanServices.updatePatchIncidents(incident.id, {
        simulation_objective: {
          title: form.title.trim(),
          description: form.description.trim(),
          success_criteria: form.success_criteria.trim(),
        },
      });
      if (res?.status !== false) {
        toast.success('Objective saved');
        setEditing(false);
        onRefresh && onRefresh(true);
      } else {
        toast.error(res?.message || 'Failed to save objective');
      }
    } catch (e) {
      console.error('objective save failed', e);
      toast.error('Failed to save objective');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="simulation-objective-widget"
      className="relative overflow-hidden rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50 p-4 shadow-sm"
    >
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-violet-200/30 blur-2xl" />
      <div className="relative flex items-start justify-between gap-3">
        <button
          type="button"
          data-testid="objective-collapse-toggle"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 text-left group"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-600 text-white shadow-sm">
            <Target className="h-4 w-4" />
          </span>
          <div>
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-1">
              Simulation / Drill Objective
              {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
            </h4>
            <p className="text-[11px] text-gray-500">What this exercise aims to validate.</p>
          </div>
        </button>
        {canEdit && !editing && !collapsed && (
          <Button
            size="sm"
            variant="outline"
            data-testid="objective-edit-btn"
            className="h-7 px-2.5 text-[11px] border-violet-300 text-violet-700 hover:bg-violet-50"
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-3.5 w-3.5 mr-1" /> {hasContent ? 'Edit' : 'Add objective'}
          </Button>
        )}
      </div>

      {!collapsed && (
      <div className="relative mt-3">
        {editing ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Title</label>
              <Input
                data-testid="objective-title-input"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Test fire evacuation readiness for Building SADIA"
                className="h-9 text-sm bg-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Description</label>
              <textarea
                data-testid="objective-description-input"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Describe the scenario goals, scope and what participants should accomplish…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 min-h-[90px] resize-y"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1">
                <ListChecks className="h-3 w-3" /> Success Criteria <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                data-testid="objective-success-input"
                value={form.success_criteria}
                onChange={(e) => setForm((p) => ({ ...p, success_criteria: e.target.value }))}
                placeholder="How will success be measured? e.g. Full evacuation under 8 minutes…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 min-h-[60px] resize-y"
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                data-testid="objective-cancel-btn"
                className="h-8 text-xs"
                onClick={() => { setEditing(false); setForm({ title: obj.title || '', description: obj.description || '', success_criteria: obj.success_criteria || '' }); }}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
              <Button
                size="sm"
                data-testid="objective-save-btn"
                className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                onClick={handleSave}
                disabled={saving}
              >
                <Check className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving…' : 'Save objective'}
              </Button>
            </div>
          </div>
        ) : hasContent ? (
          <div className="space-y-2" data-testid="objective-display">
            {obj.title && <p className="text-sm font-semibold text-gray-900">{obj.title}</p>}
            {obj.description && <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{obj.description}</p>}
            {obj.success_criteria && (
              <div className="mt-2 rounded-lg bg-white/70 border border-violet-100 p-2.5">
                <p className="text-[11px] font-bold uppercase tracking-wide text-violet-700 flex items-center gap-1 mb-1">
                  <ListChecks className="h-3 w-3" /> Success Criteria
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{obj.success_criteria}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">
            {canEdit ? 'No objective set yet. Click “Add objective” to define the drill goals.' : 'No objective defined for this drill.'}
          </p>
        )}
      </div>
      )}
    </div>
  );
};

export default SimulationObjective;
